import React, { useEffect, useMemo, useRef, useState } from "react";

// Simple football-themed memory game
// Rules (as per request):
// - No initial reveal phase
// - You have 15 seconds to find at least 3 pairs
// - If you find 3 pairs within time, you win
// - Show a message that the winner will win an Ousmane Dembélé jersey

// Use image icons from public/CardGame for the card faces (6 pairs)
const SYMBOLS = [
  "/CardGame/footbal_icon.svg",
  "/CardGame/flag.svg",
  "/CardGame/jersey.svg",
  "/CardGame/whistle.svg",
  "/CardGame/boots.svg",
  "/CardGame/staduim.svg",
  "/CardGame/golden_ballsvg.svg",
  "/CardGame/ticket-479.svg",
];

function shuffle(array) {
  const arr = array.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function buildDeck() {
  const base = SYMBOLS.flatMap((sym, idx) => [
    { id: `${idx}-a`, value: sym, matched: false, flipped: false },
    { id: `${idx}-b`, value: sym, matched: false, flipped: false },
  ]);
  return shuffle(base);
}

export default function FootballMemoryGame() {
  const [deck, setDeck] = useState(() => buildDeck());
  const [selected, setSelected] = useState([]); // indices of currently flipped (not yet matched)
  const [pairsFound, setPairsFound] = useState(0);
  const [timeLeft, setTimeLeft] = useState(15);
  const [gameStarted, setGameStarted] = useState(false);
  const [result, setResult] = useState(null); // 'win' | 'lose' | null
  const timerRef = useRef(null);

  const neededPairs = 3;
  const canInteract = gameStarted && !result;

  // Start / restart the game
  const startGame = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setDeck(buildDeck());
    setSelected([]);
    setPairsFound(0);
    setTimeLeft(15);
    setResult(null);
    setGameStarted(true);
  };

  // Timer effect
  useEffect(() => {
    if (!gameStarted || result) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => t - 1);
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameStarted, result]);

  // When time hits zero, decide outcome if not already won
  useEffect(() => {
    if (!gameStarted || result) return;
    if (timeLeft <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pairsFound >= neededPairs) {
        setResult("win");
      } else {
        setResult("lose");
      }
    }
  }, [timeLeft, gameStarted, result, pairsFound]);

  // Card click handler
  const onCardClick = (idx) => {
    if (!canInteract) return;
    const card = deck[idx];
    if (card.flipped || card.matched) return;
    if (selected.length === 2) return;

    const newDeck = deck.slice();
    newDeck[idx] = { ...card, flipped: true };
    const newSelected = [...selected, idx];
    setDeck(newDeck);
    setSelected(newSelected);

    if (newSelected.length === 2) {
      const [i1, i2] = newSelected;
      const c1 = newDeck[i1];
      const c2 = newDeck[i2];
      const isMatch = c1.value === c2.value;
      if (isMatch) {
        // Mark matched after a short delay
        setTimeout(() => {
          setDeck((current) => {
            const d = current.slice();
            d[i1] = { ...d[i1], matched: true };
            d[i2] = { ...d[i2], matched: true };
            return d;
          });
          setSelected([]);
          setPairsFound((p) => {
            const np = p + 1;
            if (np >= neededPairs) {
              if (timerRef.current) clearInterval(timerRef.current);
              setResult("win");
            }
            return np;
          });
        }, 500);
      } else {
        // Flip back after delay
        setTimeout(() => {
          setDeck((current) => {
            const d = current.slice();
            d[i1] = { ...d[i1], flipped: false };
            d[i2] = { ...d[i2], flipped: false };
            return d;
          });
          setSelected([]);
        }, 700);
      }
    }
  };

  const statusText = useMemo(() => {
    if (!gameStarted && !result) return "Press Start to play";
    if (result === "win") return "You win! Claim your Ousmane Dembélé jersey!";
    if (result === "lose") return "Time's up! Try again.";
    return `Find ${neededPairs} pairs in ${timeLeft}s`;
  }, [gameStarted, result, timeLeft]);

  return (
    <div className="min-h-screen w-full bg-gray-950 text-gray-100 flex flex-col items-center py-8 px-4">
      <div className="w-full max-w-4xl">
        <h1 className="text-3xl font-bold mb-2">Football Memory Game</h1>
        <p className="text-sm text-gray-300 mb-6">
          Find 3 pairs in 15 seconds to win an Ousmane Dembélé jersey!
        </p>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={startGame}
              className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 transition disabled:opacity-50"
            >
              {gameStarted ? "Restart" : "Start"}
            </button>
            <div className="text-sm">
              Pairs found: <span className="font-semibold">{pairsFound}</span> / {neededPairs}
            </div>
          </div>
          <div className="text-sm">
            Time left: <span className="font-semibold">{Math.max(0, timeLeft)}</span>s
          </div>
        </div>

        <div className="mb-4 text-gray-200 text-sm">{statusText}</div>

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4 select-none">
          {deck.map((card, idx) => {
            const show = card.flipped || card.matched;
            return (
              <button
                key={card.id}
                onClick={() => onCardClick(idx)}
                disabled={!canInteract || card.matched}
                className={`aspect-square rounded-lg border flex items-center justify-center text-3xl sm:text-4xl transition transform ${
                  show
                    ? "bg-green-700/20 border-green-500/40"
                    : "bg-gray-800 border-gray-700 hover:-translate-y-0.5"
                } ${card.matched ? "opacity-80" : ""}`}
              >
                {show ? (
                  <img
                    src={card.value}
                    alt="card icon"
                    className="w-2/3 h-2/3 object-contain"
                  />
                ) : (
                  <img
                    src="/favicon.ico"
                    alt="SceneShop logo"
                    className="w-10 h-10 sm:w-12 sm:h-12 opacity-90"
                  />
                )}
              </button>
            );
          })}
        </div>

        {result && (
          <div className="mt-6 p-4 rounded bg-gray-900 border border-gray-800">
            {result === "win" ? (
              <div className="text-green-400">
                Congratulations! You found {pairsFound} pair{pairsFound !== 1 ? "s" : ""}. You win an Ousmane Dembélé jersey!
              </div>
            ) : (
              <div className="text-red-400">Time's up! Find {neededPairs} pairs to win the jersey.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
